import { memo, useLayoutEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { TimelineTickMark } from '../utils/use-ticks';
import { TICKS_HEIGHT } from './constants';

type TickHeaderProps = {
  tick: TimelineTickMark;
};

const tickStyle: CSSProperties = {
  height: TICKS_HEIGHT - 10,
};

const SUB_TICK_MIN_WIDTH_PX = 12;
const SUB_TICK_CANDIDATES = [10, 5, 2] as const;

function getSubdivisionCount(tickWidth: number): number {
  for (const count of SUB_TICK_CANDIDATES) {
    if (tickWidth / count >= SUB_TICK_MIN_WIDTH_PX) {
      return count;
    }
  }
  return 0;
}

const MINOR_TICK_HEIGHT = 6;
const MID_TICK_HEIGHT = 10;

const TickHeaderComponent = ({ tick }: TickHeaderProps) => {
  const tickRef = useRef<HTMLDivElement>(null);
  const subdivisions = getSubdivisionCount(tick.width);

  useLayoutEffect(() => {
    if (tickRef.current) {
      tickRef.current.style.width = tick.width + 'px';
      tickRef.current.style.minWidth = tick.width + 'px';
    }
  }, [tick.width]);

  return (
    <div
      className="relative border-l border-dotted border-l-sys-style-neutral-default"
      ref={tickRef}
    >
      <div
        className="flex items-end truncate bg-sys-bg-surface pt-3 pl-1 text-comp-moderate-xsmall text-sys-cont-neutral-lightest"
        style={tickStyle}
      >
        {tick.label}
      </div>
      {subdivisions > 0 &&
        Array.from({ length: subdivisions - 1 }, (_, i) => {
          const positionPercent = ((i + 1) / subdivisions) * 100;
          const isMidpoint =
            subdivisions % 2 === 0 && i + 1 === subdivisions / 2;
          return (
            <div
              key={i}
              className="absolute bottom-0 bg-sys-style-neutral-default"
              style={{
                left: `${positionPercent}%`,
                height: isMidpoint ? MID_TICK_HEIGHT : MINOR_TICK_HEIGHT,
                width: isMidpoint ? 2 : 1,
              }}
            />
          );
        })}
    </div>
  );
};

TickHeaderComponent.displayName = 'TickHeader';

export const TickHeader = memo(TickHeaderComponent);
